import React from 'react';
import PropTypes from 'prop-types';

const Skeleton = ({ 
  variant = 'text', 
  width = '100%', 
  height, 
  className = '',
  count = 1,
  style = {}
}) => {
  const getSkeletonHeight = () => {
    if (height) return height;
    switch (variant) {
      case 'text': return '1em';
      case 'title': return '1.5em';
      case 'subtitle': return '1.2em';
      case 'avatar': return '40px';
      case 'avatar-large': return '200px';
      case 'card': return '200px';
      case 'button': return '40px';
      default: return '1em';
    }
  };

  const getSkeletonClass = () => {
    const baseClass = 'skeleton';
    switch (variant) {
      case 'avatar':
      case 'avatar-large':
        return `${baseClass} skeleton-avatar ${variant === 'avatar-large' ? 'large' : ''}`;
      case 'card':
        return `${baseClass} skeleton-card`;
      case 'button':
        return `${baseClass} skeleton-button`;
      default:
        return `${baseClass} skeleton-text`;
    }
  };

  const skeletonStyle = {
    width,
    height: getSkeletonHeight(),
    ...style
  };

  if (count === 1) {
    return (
      <div 
        className={`${getSkeletonClass()} ${className}`}
        style={skeletonStyle}
      />
    );
  }

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div 
          key={index}
          className={`${getSkeletonClass()} ${className}`}
          style={skeletonStyle}
        />
      ))}
    </>
  );
};

// Composant SkeletonCard pour les cartes complètes
export const SkeletonCard = ({ showAvatar = false, lines = 3 }) => (
  <div className="skeleton-card">
    {showAvatar && <Skeleton variant="avatar" className="mb-md" />}
    <Skeleton variant="title" className="mb-sm" width="80%" />
    <Skeleton variant="text" count={lines} className="mb-xs" />
  </div>
);

// Composant SkeletonList pour les listes
export const SkeletonList = ({ items = 3, showAvatar = false }) => {
  return (
    <div>
      {Array.from({ length: items }, (_, index) => (
        <div key={index} className="skeleton-list-item" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 'var(--spacing-md)', 
          marginBottom: 'var(--spacing-lg)' 
        }}>
          {showAvatar && <Skeleton variant="avatar" />}
          <div style={{ flex: 1 }}>
            <Skeleton variant="text" width="70%" className="mb-xs" />
            <Skeleton variant="text" width="50%" />
          </div>
        </div>
      ))}
    </div>
  );
};

// PropTypes
Skeleton.propTypes = {
  variant: PropTypes.oneOf(['text', 'title', 'subtitle', 'avatar', 'avatar-large', 'card', 'button']),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  count: PropTypes.number,
  style: PropTypes.object,
};

SkeletonCard.propTypes = {
  showAvatar: PropTypes.bool,
  lines: PropTypes.number,
};

SkeletonList.propTypes = {
  items: PropTypes.number,
  showAvatar: PropTypes.bool,
};

export default Skeleton;
